import { gsap } from 'gsap'
import { useCallback, useEffect, useRef } from 'react'
import useDelayedActiveState from 'lib/react/hooks/useDelayedActiveState'
import { POSITIONS } from './constants'

/**
 * The `useBurgerAnimation`
 *
 * @param {object} ref - the ref
 * @param {boolean} isBurgerMenuOpen - whether the burger menu is open
 * @returns {boolean} whether the burger is active
 */
const useBurgerAnimation = (ref, isBurgerMenuOpen) => {
  const isActive = useDelayedActiveState(isBurgerMenuOpen, 0.2, 0.1)
  const wasActive = useRef(false)
  const isMounted = useRef(false)
  const pos = useRef({
    top: {
      ...POSITIONS.top.inactive
    },
    middle: {
      ...POSITIONS.middle.inactive
    },
    bottom: {
      ...POSITIONS.bottom.inactive
    }
  })
  const refTop = useRef(null)
  const refMiddle = useRef(null)
  const refBottom = useRef(null)

  /**
   * Update the top line
   */
  const handleUpdatePositionTop = useCallback(() => {
    if (!refTop.current) {
      refTop.current = ref.current.querySelector('path:nth-child(1)')
    }

    const { x1, x2, y1, y2 } = pos.current.top

    refTop.current.setAttribute('d', `M${x1},${y1} L${x2},${y2} Z`)
  }, [])

  /**
   * Update the middle line
   */
  const handleUpdatePositionMiddle = useCallback(() => {
    if (!refMiddle.current) {
      refMiddle.current = ref.current.querySelector('path:nth-child(2)')
    }

    const { x1, x2, y1, y2 } = pos.current.middle

    refMiddle.current.setAttribute('d', `M${x1},${y1} L${x2},${y2} Z`)
  }, [])

  /**
   * Update the bottom line
   */
  const handleUpdatePositionBottom = useCallback(() => {
    if (!refBottom.current) {
      refBottom.current = ref.current.querySelector('path:nth-child(3)')
    }

    const { x1, x2, y1, y2 } = pos.current.bottom

    refBottom.current.setAttribute('d', `M${x1},${y1} L${x2},${y2} Z`)
  }, [])

  /**
   * Animate the switch between states
   */
  const setIsActive = useCallback(isActive => {
    gsap.to(pos.current.top, {
      ...(isActive ? POSITIONS.top.active : POSITIONS.top.inactive),
      ease: 'cubic.inOut',
      duration: 0.25,
      onUpdate: handleUpdatePositionTop
    })

    gsap.to(pos.current.middle, {
      ...(isActive ? POSITIONS.middle.active : POSITIONS.middle.inactive),
      ease: 'cubic.inOut',
      duration: 0.25,
      onUpdate: handleUpdatePositionMiddle
    })

    gsap.to(pos.current.bottom, {
      ...(isActive ? POSITIONS.bottom.active : POSITIONS.bottom.inactive),
      ease: 'cubic.inOut',
      duration: 0.25,
      onUpdate: handleUpdatePositionBottom
    })
  }, [])

  /**
   * Effect: When the `isActive` state changes
   */
  useEffect(() => {
    if (isActive === wasActive.current) {
      return
    }

    wasActive.current = isActive

    setIsActive(isActive)
  }, [isActive])

  /**
   * Effect: On mount/unmount
   */
  useEffect(() => {
    wasActive.current = false
    isMounted.current = true

    return () => {
      gsap.killTweensOf(pos.current.top)
      gsap.killTweensOf(pos.current.top)

      isMounted.current = false
    }
  }, [])

  return isActive
}

export default useBurgerAnimation
